/* This grid is treatments filter pane grid and handles the particular selection behavior
used by this grid.
*/
import {
	Div,
	Input,
	reactive,
	Span,
	VString
} from 'alkali'
import { Grid } from 'components/grid/Grid'
import { Tree } from 'components/grid/Tree'
import * as controlIcon from 'icons/pill.svg'
import Icon from 'components/Icon'
import { addTreatment, interventionsAvailable, selectedTreatmentIds } from 'model/EvidenceSearch'
import { analyzeTreatmentAs, faqOrReadOnly, structuredQuery } from 'model/SearchQuery'
import { InterventionDropdown, renderInitial } from './InterventionDropdown'
import { TermEditorIcon } from './TermEditorIcon'
import { groupBy } from 'lodash'
import app from 'app/app'
import { HasFilter } from './HasFilter'
import { CONTROL_IDS } from 'analysis/assignGroupArms'

const selectedIntervention = reactive('')
const interventionCollections = reactive([])
export const interventionDropdown = new InterventionDropdown({
	selected: selectedIntervention,
	interventionCollections,
	allowSelected: true
})

export class InterventionsGrid extends HasFilter(Tree(Grid)).with('.intervention-grid', {
	loadingMessage: 'Loading...',
	selectionMode: 'toggle',
	deselectOnRefresh: false,
	selected: selectedTreatmentIds,
	placeholder: 'What is the effect of ...',
	label: 'Treatment',
	attributeType: 'intervention',
	id: 'intervention-filter-grid',
}) {
	ready(properties) {
		this.searchText = reactive('')
		this.collection = interventionsAvailable
		this.topScore = this.collection.to(collection =>
			Math.max(...collection.map(asScore)))
		this.addTerm = addTreatment
		this.columns = this.columns || {
			selector: {
				label: '',
				flex: '0 0 18px',
				width: 18,
				selector: (column, selected, cell, data) => {
					let selectedVar = reactive(selected)
					data.selectedVar = selectedVar
					let selector = new Div('.selector-checkbox', {
						classes: { 'selector-selected': selectedVar },
						content: selectedVar.to(selected => analyzeTreatmentAs.to(analyzeTreatmentAs => selected ?
							renderInitial(analyzeTreatmentAs.get(data.id) || (data.label || data.name)) : null)),
						title: selectedVar.to(selected => analyzeTreatmentAs.to(analyzeTreatmentAs => selected ? analyzeTreatmentAs.get(data.id) ?
							'Analyzed as intervention group ' + analyzeTreatmentAs.get(data.id) + '. ' : '' : 'Not selected')),
						onclick: (event) => {
							if (faqOrReadOnly.valueOf())
								return
							this.select(event, null, null) // toggle selection
						}
					})
					Object.defineProperty(selector, 'checked', {
						set: (checked) => {
							selectedVar.put(checked)
						}
					})
					cell.append(selector)
					cell.input = selector
				}
			},
			menu: {
				label: '☰',
				flex: '0 0 18px',
				width: 18,
				renderCell: (data) =>
					new Span('.selection-menu-icon', {
						content: '☰',
						title: 'Click to customize how this treatment is included in analysis',
						onpointerdown(event) {
							event.stopPropagation()
						},
						onpointerup(event) {
							event.stopPropagation()
						},
						onclick: (event) => {
							if (faqOrReadOnly.valueOf())
								return
							let row = this.row(event)
							for (let [ key, label ] of analyzeTreatmentAs) {
								if (label && !interventionCollections.valueOf().includes(label))
									interventionCollections.push(label)
							}
							let data = row.data
							let selectedVar = data.selectedVar
							let defaultLabel = data.label || data.id
							selectedIntervention.put(defaultLabel)
							interventionDropdown.show(event)
							interventionDropdown.onSelection = (asGroup) => {
								if (asGroup) {
									let oldInterventions = structuredQuery.get('interventions')
									analyzeTreatmentAs.set(data.id, asGroup)
									selectedVar.updated()
									let isSelected = Boolean(asGroup)
									selectedVar.put(isSelected)
								} else {
									this.deselect(row)
								}
							}
						}
					})
			},
			expand: {
				label: '',
				flex: '0 0 1rem',
				width: 16,
				renderExpando: true,
			},
			label: {
				flex: '0.8 0.8 100px',
				width: '80%',
				get(term) {
					return term.label
				},
				renderCell(data, label) {
					return new Span({
						content: label,
						title: data.definition || label,
					})
				}
			},
			editConcept: {
				flex: '0 0 20px',
				width: 20,
				renderCell(data) {
					if (app.user?.FeatureConfig?.['Bindings Admin']?.Enabled)
						return new TermEditorIcon({ term: data, type: 'intervention' })
				}
			},
		}
/* I don't think we really want to expand just when clicking on a row
		this.on('.dgrid-row:click', event => {
			if (event.target.classList.contains('dgrid-expando-icon'))
				return
			let row = this.row(event)
			if (!this._expanded[row.id]) {
				this.expand(row)
			}
		})*/
		super.ready(properties)
	}
	shouldExpand(row, level, previouslyExpanded) {
		let ids = this.selected.valueOf()
		return previouslyExpanded || ids && ids.some(id => id && id.startsWith(row.id)  && id.length > row.id.length)
	}
	_handleSelect(event, target) {
		// if we only have auto-selections, clear them when explicit selections are made
		if (faqOrReadOnly.valueOf())
			return
		if (!(structuredQuery.valueOf()?.interventions?.length > 0)) {
			this.clearSelection()
		}
		return super._handleSelect(event, target)
	}
	getChildren(data) {
		return data.children
	}
	mayHaveChildren(data) {
		return data.children
	}
	getIdentity(data) {
		return data.id
	}
	renderRow(item, options) {
		let row = super.renderRow(item, options)
		row.append(Div('.attribute-usage-bar.mymove', {
			width: asScore(item) / this.topScore * 90 + '%',
		}))
		row.querySelector('.dgrid-expando-icon').title = 'Click to view combination treatments and synonyms'
		return row
	}
}
const asScore = entry => Math.sqrt(entry.score)
